import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { supplierService } from '../../services/supplierService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import LazyTableWithMonthGroups from '../../components/Common/LazyTableWithMonthGroups';
import { useLazyMonthList } from '../../hooks/useLazyMonthList';
import { appendComprasSiteParams } from '../../utils/comprasListParams';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import { isPurchaseOrderReadOnly, canReopenPurchaseOrder } from '../../utils/purchaseOrderProgress';
import PurchaseOrderPdfButton from '../../components/PurchaseOrders/PurchaseOrderPdfButton';
import { formatLinkedRequirementCodes } from '../../utils/purchaseOrderRequirements';
import BuyerUserCell from '../../components/Common/BuyerUserCell';
import ComprasProjectFilterSelect from '../../components/Common/ComprasProjectFilterSelect';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { formatLineProjectsLabel } from '../../utils/lineProjectLabels';
import ComprasSiteFilterTabs from '../../components/Common/ComprasSiteFilterTabs';
import { useComprasSiteListParams, useComprasSiteViewState } from '../../utils/comprasPurchaseSite';
import { formatListStatsAmount } from '../../utils/documentCurrency';

const PurchaseOrders = () => {
  const canMutate = useCanMutate();
  const { productsProjectEnabled } = useModuleSettings();
  const { siteView, onSiteViewChange } = useComprasSiteViewState();
  const siteListParams = useComprasSiteListParams(siteView);
  const purchaseSiteId = siteListParams.purchase_site_id ?? null;
  const withoutPurchaseSite = siteListParams.without_purchase_site ? 1 : 0;
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [orderToToggle, setOrderToToggle] = useState(null);
  const [isCloseFulfillmentModalOpen, setIsCloseFulfillmentModalOpen] = useState(false);
  const [orderToCloseFulfillment, setOrderToCloseFulfillment] = useState(null);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [orderToReopen, setOrderToReopen] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const buildListParams = useCallback(() => {
    const params = {};
    appendComprasSiteParams(params, purchaseSiteId, withoutPurchaseSite);
    if (statusFilter !== 'all') params.status = statusFilter;
    if (selectedSupplier) params.supplier_id = selectedSupplier;
    if (userFilter !== 'all') params.created_by = userFilter;
    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (productsProjectEnabled && projectFilter !== 'all') params.project_id = projectFilter;
    return params;
  }, [
    purchaseSiteId,
    withoutPurchaseSite,
    statusFilter,
    selectedSupplier,
    userFilter,
    searchTerm,
    dateFrom,
    dateTo,
    productsProjectEnabled,
    projectFilter,
  ]);

  const fetchSummary = useCallback(
    (params) => purchaseOrderService.getPurchaseOrders({ ...params, month_summary: 1 }),
    []
  );
  const fetchMonth = useCallback((params) => purchaseOrderService.getPurchaseOrders(params), []);

  const {
    monthSummaries,
    listStats,
    creatorOptions,
    loadingSummary,
    loadMonthDetail,
    getMonthItems,
    isMonthLoading,
    reloadSummary,
  } = useLazyMonthList({
    buildParams: buildListParams,
    fetchSummary,
    fetchMonth,
    searchTerm,
  });

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    if (!productsProjectEnabled) return;
    purchaseOrderService.getFormData().then((res) => {
      if (res.success) setProjects(res.data?.projects || []);
    }).catch(() => {});
  }, [productsProjectEnabled]);

  const loadSuppliers = async () => {
    console.log('🔍 [PurchaseOrders/Index] loadSuppliers() llamado');
    console.log('📍 [PurchaseOrders/Index] Razón: Cargar lista de proveedores para el filtro de búsqueda');
    console.log('⏰ [PurchaseOrders/Index] Timestamp:', new Date().toISOString());
    
    try {
      console.log('📡 [PurchaseOrders/Index] Llamando a supplierService.getSuppliers() con params:', { active_only: true });
      const response = await supplierService.getSuppliers({ active_only: true });
      
      console.log('✅ [PurchaseOrders/Index] Respuesta recibida de supplierService');
      console.log('📦 [PurchaseOrders/Index] Response:', response);
      
      if (response.success) {
        console.log('✅ [PurchaseOrders/Index] response.success = true');
        console.log('📊 [PurchaseOrders/Index] Cantidad de proveedores recibidos:', response.data?.length || 0);
        setSuppliers(response.data);
        console.log('✅ [PurchaseOrders/Index] Estado suppliers actualizado con', response.data?.length || 0, 'proveedores');
      } else {
        console.warn('⚠️ [PurchaseOrders/Index] response.success = false');
        console.warn('📋 [PurchaseOrders/Index] Response completa:', response);
      }
    } catch (error) {
      console.error('❌ [PurchaseOrders/Index] Error al cargar proveedores:', error);
      console.error('📊 [PurchaseOrders/Index] Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
    }
  };

  const handleToggleStatusClick = (order) => {
    setOrderToToggle(order);
    setIsToggleModalOpen(true);
  };

  const handleCloseFulfillmentClick = (order) => {
    setOrderToCloseFulfillment(order);
    setIsCloseFulfillmentModalOpen(true);
  };

  const handleReopenClick = (order) => {
    setOrderToReopen(order);
    setIsReopenModalOpen(true);
  };

  const handleReopenConfirm = async () => {
    if (!orderToReopen) return;
    setLoading(true);
    try {
      const response = await purchaseOrderService.reopenFulfillment(orderToReopen.id);
      await reloadSummary();
      setIsReopenModalOpen(false);
      setOrderToReopen(null);
      setToast({
        isOpen: true,
        message: response.message || 'Orden reabierta correctamente',
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message:
          error.response?.data?.message || 'No se pudo reabrir la orden de compra',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseFulfillmentConfirm = async () => {
    if (!orderToCloseFulfillment) return;
    setLoading(true);
    try {
      const response = await purchaseOrderService.closeFulfillment(orderToCloseFulfillment.id, {
        notes: 'Cerrada desde listado: no habrá más documentos por el saldo pendiente de la orden.',
      });
      await reloadSummary();
      setIsCloseFulfillmentModalOpen(false);
      setOrderToCloseFulfillment(null);
      setToast({
        isOpen: true,
        message: response.message || 'Orden marcada como completada',
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message:
          error.response?.data?.message || 'No se pudo cerrar la orden de compra',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatusConfirm = async () => {
    if (!orderToToggle) return;
    
    setLoading(true);
    try {
      const response = await purchaseOrderService.togglePurchaseOrderStatus(orderToToggle.id);
      await reloadSummary();
      setIsToggleModalOpen(false);
      setOrderToToggle(null);
      setToast({
        isOpen: true,
        message: response.message || (orderToToggle.status === 'cancelled' 
          ? 'Orden de compra reactivada exitosamente' 
          : 'Orden de compra cancelada exitosamente'),
        type: 'success'
      });
    } catch (error) {
      console.error('Error al cambiar estado de orden:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || error.message || 'Error al cambiar el estado de la orden de compra',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const totalOrders = listStats?.total ?? 0;
  const issuedOrders = listStats?.issued ?? 0;
  const cancelledOrders = listStats?.cancelled ?? 0;

  // Calcular progreso de items basado en cantidad usada en documentos
  const getProgressPercentage = (order) => {
    if (!order.items || order.items.length === 0) return 0;
    
    const totalQuantity = order.items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
    
    // Calcular cantidad usada: quantity - pending_quantity
    const usedQuantity = order.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity || 0);
      const pendingQty = item.pending_quantity !== undefined ? parseFloat(item.pending_quantity || 0) : qty;
      const usedQty = qty - pendingQty;
      return sum + usedQty;
    }, 0);
    
    return totalQuantity > 0 ? Math.round((usedQuantity / totalQuantity) * 100) : 0;
  };

  // Verificar si la orden tiene progreso (ya se ha usado en documentos)
  const hasProgress = (order) => {
    if (!order.items || order.items.length === 0) return false;
    
    // Verificar si algún item tiene pending_quantity menor que quantity
    return order.items.some(item => {
      const qty = parseFloat(item.quantity || 0);
      const pendingQty = item.pending_quantity !== undefined ? parseFloat(item.pending_quantity || 0) : qty;
      return pendingQty < qty;
    });
  };

  const handlePdfSuccess = (order) => {
    setToast({
      isOpen: true,
      message: `PDF de ${order.code} descargado`,
      type: 'success',
    });
  };

  const handlePdfError = (message) => {
    setToast({
      isOpen: true,
      message: message || 'Error al generar el PDF',
      type: 'error',
    });
  };

  const renderPdfAction = (order) => (
    <PurchaseOrderPdfButton
      orderId={order.id}
      onSuccess={handlePdfSuccess}
      onError={handlePdfError}
    />
  );

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Órdenes de Compra</h1>
                <p className="text-gray-600 mt-1">Gestión de órdenes de compra a proveedores</p>
              </div>
              <MutateGate>
                <Link
                  to="/purchase-orders/create"
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors inline-block"
                >
                  + Nueva Orden de Compra
                </Link>
              </MutateGate>
            </div>

            <ComprasSiteFilterTabs siteView={siteView} onSiteViewChange={onSiteViewChange} />

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/compras" className="hover:text-teal-600">Compras</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Órdenes de Compra</span>
            </nav>

            {/* Filtros, Búsqueda y Rango de Fechas */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                {/* Filtro por Estado */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Estados</option>
                  <option value="issued">Emitidas</option>
                  <option value="cancelled">Canceladas</option>
                </select>

                {/* Filtro por Usuario */}
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Usuarios</option>
                  {creatorOptions.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>

                {productsProjectEnabled && (
                  <ComprasProjectFilterSelect
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    projects={projects}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                )}

                {/* Búsqueda */}
                <input
                  type="text"
                  placeholder="Buscar por código o proveedor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />

                {/* Fecha Desde */}
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Fecha desde"
                />

                {/* Fecha Hasta */}
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Fecha hasta"
                />
              </div>
            </div>

            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Total</div>
                <div className="text-2xl font-bold text-gray-800">{totalOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Emitidas</div>
                <div className="text-2xl font-bold text-teal-600">{issuedOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Canceladas</div>
                <div className="text-2xl font-bold text-gray-500">{cancelledOrders}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Monto Total</div>
                <div className="text-2xl font-bold text-blue-600">{formatListStatsAmount(listStats)}</div>
              </div>
            </div>
          </div>

          {/* Tabla de Órdenes */}
          {loadingSummary && monthSummaries.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">Cargando resumen por mes…</div>
            </div>
          ) : monthSummaries.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No se encontraron órdenes de compra</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden relative">
              {loadingSummary && (
                <div className="absolute top-2 right-3 z-10 text-xs text-gray-500 bg-white/90 px-2 py-1 rounded border border-gray-200">
                  Actualizando…
                </div>
              )}
              <LazyTableWithMonthGroups
                months={monthSummaries}
                getMonthItems={getMonthItems}
                isMonthLoading={isMonthLoading}
                onMonthExpand={loadMonthDetail}
                defaultCollapsed
                columnCount={productsProjectEnabled ? 10 : 9}
                wrapperClassName="w-full"
                className="!w-full table-fixed"
                colgroup={(
                  <colgroup>
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '27%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '11%' }} />
                  </colgroup>
                )}
                emptyMessage="No se encontraron órdenes de compra"
                headerRow={
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entidad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requerimiento</th>
                    {productsProjectEnabled && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proyecto</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Orden</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progreso</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                }
                renderRow={(order) => {
                  const progress = order.fulfillment_percent ?? getProgressPercentage(order);
                  const isFulfillmentClosed =
                    Boolean(order.fulfillment_closed_at) || order.is_fulfillment_closed;
                  const canReopen = canReopenPurchaseOrder(order);
                  const readOnlyOrder = !canReopen && isPurchaseOrderReadOnly(order);
                  const orderHasProgress =
                    !canReopen && !readOnlyOrder && (hasProgress(order) || progress > 0);
                  const statusLabel = order.status === 'cancelled'
                    ? 'Cancelada'
                    : isFulfillmentClosed
                    ? 'Cerrada'
                    : progress >= 100
                    ? 'Completada'
                    : progress > 0
                    ? 'Parcial'
                    : 'Emitida';
                  const statusClass = order.status === 'cancelled'
                    ? 'bg-red-100 text-red-800'
                    : isFulfillmentClosed
                    ? 'bg-slate-100 text-slate-800'
                    : progress >= 100
                    ? 'bg-blue-100 text-blue-800'
                    : progress > 0
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-green-100 text-green-800';
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        {canReopen ? (
                          <span className="text-sm font-mono text-gray-600">{order.code}</span>
                        ) : readOnlyOrder ? (
                          <Link
                            to={`/purchase-orders/${order.id}/edit?modo=ver`}
                            className="text-sm font-mono text-teal-600 hover:text-teal-900"
                          >
                            {order.code}
                          </Link>
                        ) : orderHasProgress ? (
                          <span className="text-sm font-mono text-gray-600">{order.code}</span>
                        ) : (
                          <Link
                            to={`/purchase-orders/${order.id}/edit`}
                            className="text-sm font-mono text-teal-600 hover:text-teal-900"
                          >
                            {order.code}
                          </Link>
                        )}
                        <BuyerUserCell creator={order.creator} />
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900 break-words leading-snug">{order.supplier?.name || 'N/A'}</div>
                        {order.supplier?.ruc && <div className="text-xs text-gray-500">RUC: {order.supplier.ruc}</div>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-teal-700">
                        {formatLinkedRequirementCodes(order) || <span className="text-gray-400">—</span>}
                      </td>
                      {productsProjectEnabled && (
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {formatLineProjectsLabel(order.items)}
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateLocaleLima(order.order_date)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{order.items?.length || 0} items</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div className="bg-teal-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                          </div>
                          <span className="text-xs text-gray-600">{progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        S/ {parseFloat(order.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm font-medium">
                        {canReopen ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <MutateGate>
                              <button
                                type="button"
                                onClick={() => handleReopenClick(order)}
                                className="text-teal-600 hover:text-teal-900 font-medium"
                                title="Reabrir la orden y restaurar su estado de avance anterior"
                              >
                                Abrir
                              </button>
                            </MutateGate>
                            {renderPdfAction(order)}
                          </div>
                        ) : readOnlyOrder ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/purchase-orders/${order.id}/edit?modo=ver`}
                              className="text-teal-600 hover:text-teal-900"
                              title="Ver orden (solo lectura)"
                            >
                              Ver
                            </Link>
                            {renderPdfAction(order)}
                          </div>
                        ) : orderHasProgress ? (
                          <div className="flex flex-wrap items-center gap-2">
                            {renderPdfAction(order)}
                            <MutateGate>
                              <button
                                type="button"
                                onClick={() => handleCloseFulfillmentClick(order)}
                                className="text-amber-700 hover:text-amber-900 whitespace-nowrap"
                                title="No habrá más documentos: dar la orden por completada"
                              >
                                Cerrar como completada
                              </button>
                            </MutateGate>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/purchase-orders/${order.id}/edit`}
                              className="text-purple-600 hover:text-purple-900"
                              title={canMutate ? 'Editar' : 'Ver detalle'}
                            >
                              {canMutate ? 'Editar' : 'Ver'}
                            </Link>
                            {renderPdfAction(order)}
                            <MutateGate>
                              <button type="button" onClick={() => handleToggleStatusClick(order)} className={order.status === 'issued' ? "text-orange-600 hover:text-orange-900" : "text-green-600 hover:text-green-900"} title={order.status === 'issued' ? 'Cancelar' : 'Reactivar'}>
                                {order.status === 'issued' ? 'Cancelar' : 'Reactivar'}
                              </button>
                            </MutateGate>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={isReopenModalOpen}
        onClose={() => {
          setIsReopenModalOpen(false);
          setOrderToReopen(null);
        }}
        onConfirm={handleReopenConfirm}
        title="Reabrir orden de compra"
        message={
          orderToReopen
            ? `¿Reabrir la orden "${orderToReopen.code}"? Volverá a su estado anterior según documentos y guías registradas (Emitida o Parcial) y podrá registrar más comprobantes o cerrarla de nuevo.`
            : ''
        }
        confirmText="Sí, reabrir"
        cancelText="No"
      />

      <ConfirmModal
        isOpen={isCloseFulfillmentModalOpen}
        onClose={() => {
          setIsCloseFulfillmentModalOpen(false);
          setOrderToCloseFulfillment(null);
        }}
        onConfirm={handleCloseFulfillmentConfirm}
        title="Cerrar orden de compra"
        message={
          orderToCloseFulfillment
            ? `¿Marcar "${orderToCloseFulfillment.code}" como completada? Use esta opción si ya no registrará más documentos por el saldo pendiente (la orden dejó de estar "en uso" para nuevos comprobantes).`
            : ''
        }
        confirmText="Sí, cerrar orden"
        cancelText="No"
      />

      {/* Modal de confirmación de cambio de estado */}
      <ConfirmModal
        isOpen={isToggleModalOpen}
        onClose={() => {
          setIsToggleModalOpen(false);
          setOrderToToggle(null);
        }}
        onConfirm={handleToggleStatusConfirm}
        title={orderToToggle?.status === 'issued' ? 'Cancelar Orden de Compra' : 'Reactivar Orden de Compra'}
        message={orderToToggle?.status === 'issued' 
          ? `¿Estás seguro de que deseas cancelar la orden "${orderToToggle?.code}"?`
          : `¿Estás seguro de que deseas reactivar la orden "${orderToToggle?.code}"?`}
        confirmText={orderToToggle?.status === 'issued' ? 'Cancelar Orden' : 'Reactivar Orden'}
        cancelText="No"
        isLoading={loading}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default PurchaseOrders;


